import classNames from "classnames";
import { DataSourceSelector } from "features/DataSourceSelector";
import { IconLoader } from "components/Icon";
import SearchModeSelector from "components/SearchModeSelector";
import { Button } from "design_system";
import { FilterButton } from "features/FilterDrawer";
import { ArrowRight } from "lucide-react";
import React from "react";
import TextArea, { TextareaProps } from "react-expanding-textarea";
import AttachedThreadInteractionSources from "features/AskFollowUpQuestionInput/components/ThreadInteractionSources";
import { selectSources } from "store/slices/sources";
import { useAppSelector } from "hooks/useStore";
import useLabels from "hooks/useLabels";

const ONE_LINER_MAX_TEXT_HEIGHT = 24;

interface SearchInputFieldProps extends TextareaProps {
  isHomePage: boolean;
  isScrolledDown: boolean;
  focused: boolean;
  isSearching: boolean;
  onClear: () => void;
  onSubmit: () => void;
  showDataSourceSelector?: boolean;
}

export const SearchInputField = React.forwardRef<
  HTMLInputElement,
  SearchInputFieldProps
>(
  (
    {
      isHomePage,
      isScrolledDown,
      focused,
      isSearching,
      onClear,
      onSubmit,
      value,
      showDataSourceSelector = false,
      ...restProps
    },
    ref
  ) => {
    const sources = useAppSelector(selectSources);
    const [labels] = useLabels("features.ask-follow-up-question-input");

    const inputHeight = (ref as React.RefObject<HTMLInputElement>)?.current
      ?.offsetHeight as number;
    const isLongQuery = inputHeight > ONE_LINER_MAX_TEXT_HEIGHT;
    const isOutlineExpanded = !isHomePage && isScrolledDown;

    return (
      <div
        data-test-id="search-input-box"
        id="search-input-box"
        className={classNames(
          isHomePage
            ? "flex flex-col w-full h-full max-w-page pl-4 pr-3 pb-2 space-y-2 bg-[var(--color-component-search-input-background)] border border-border-base focus-within:border-border-emphasis rounded-2xl shadow-md shadow-subtle"
            : "w-full px-3 md:px-4 bg-[var(--color-component-search-input-background)] rounded-2xl transition border border-base outline-solid transition-[outline-width] duration-150 ease-out co-bottom-shadow py-1.5 hover:border-accent-border-base outline-border-base",
          !isHomePage && isOutlineExpanded ? "outline-8" : "outline-0",
          sources.length > 0 ? "pt-2" : "pt-4"
        )}
      >
        <AttachedThreadInteractionSources className="-ml-2 mb-3" />
        <div className={`flex ${isLongQuery ? "items-start" : "items-center"}`}>
          <TextArea
            value={value}
            ref={ref as React.Ref<HTMLTextAreaElement>}
            placeholder={
              sources.length === 1
                ? labels["ask-one-paper"]
                : sources.length > 1
                  ? labels["ask-multiple-papers"]
                  : labels["general"]
            }
            {...restProps}
            className="block overflow-hidden w-full resize-none outline-hidden"
          />
          {!isHomePage && (
            <div className={`grid ${!isHomePage ? "grid-cols-2" : ""}`}>
              <div className={`flex items-center ml-1`}>
                {value && (
                  <button
                    data-testid="close-input"
                    onClick={onClear}
                    type="button"
                    className="inline-flex justify-center items-center w-10 h-10 rounded-full hover:bg-bg-faint"
                  >
                    <i className="text-xl icon-x" />
                  </button>
                )}
              </div>
              <button
                data-testid="search-button"
                aria-label="Submit search"
                id="search-button"
                className="inline-flex justify-center items-center w-10 h-10 rounded-full ml-[6px] hover:bg-bg-faint"
                onClick={onSubmit}
              >
                {isSearching ? (
                  <div className="-mr-3 w-6 h-6">
                    <IconLoader />
                  </div>
                ) : (
                  <i className="text-xl icon-search" />
                )}
              </button>
            </div>
          )}
        </div>
        {isHomePage ? (
          <div className="flex justify-between pt-2">
            <div className="flex gap-2 items-center">
              <span className="flex items-center">
                <div className="flex items-center -ml-3">
                  {showDataSourceSelector && <DataSourceSelector />}
                  <SearchModeSelector
                    hideLabelForMobile={showDataSourceSelector}
                    className={showDataSourceSelector ? "-ml-1" : undefined}
                  />
                </div>
              </span>
            </div>
            <div className="flex items-center">
              <div className="mr-2">
                <FilterButton hideLabelForMobile />
              </div>
              <Button
                data-testid="search-button"
                id="search-button"
                aria-label="Submit search"
                variant="primary"
                size="sm"
                iconLeft={<ArrowRight />}
                iconOnly
                onClick={onSubmit}
                isDisabled={isSearching || !value}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }
);

SearchInputField.displayName = "SearchInputField";
